import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Loading from '@/components/common/Loading'
import Badge from '@/components/common/Badge'
import { fetchAiLabRun } from '@/lib/aiLabClient'

const formatNumber = (value, digits = 2) => {
  if (value === null || value === undefined) return '—'
  return Number(value).toFixed(digits)
}

const formatDateTime = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  return date.toLocaleString()
}

const metricLabels = {
  discovery: 'Discovery',
  navigation: 'Navigation',
  structure: 'Structure',
  ical: 'iCal',
  listView: 'List View',
  extraction: 'Extraction',
  teamEnum: 'Team Enum',
  speed: 'Speed',
  cost: 'Cost',
  total: 'Total',
}

function AILabResults() {
  const location = useLocation()
  const navigate = useNavigate()
  const search = new URLSearchParams(location.search)
  const runId = search.get('runId')

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [expandedCaseId, setExpandedCaseId] = useState(null)
  const [polling, setPolling] = useState(false)

  const loadRun = async () => {
    if (!runId) {
      toast.error('Missing runId parameter')
      navigate('/admin/ai-lab')
      return
    }

    setLoading(true)
    try {
      const runData = await fetchAiLabRun(runId)
      setData(runData)
      
      // Start polling if run is not complete
      if (runData.run.status === 'running') {
        setPolling(true)
      }
    } catch (err) {
      console.error('Failed to load run results', err)
      toast.error(err.message || 'Failed to load run results')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRun()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId])

  // Poll for updates when run is still in progress
  useEffect(() => {
    if (!polling || !runId) return

    const interval = setInterval(async () => {
      try {
        const runData = await fetchAiLabRun(runId)
        setData(runData)
        
        // Stop polling when complete
        if (runData.run.status !== 'running') {
          setPolling(false)
        }
      } catch (err) {
        console.error('Polling error', err)
        setPolling(false)
      }
    }, 5000) // Poll every 5 seconds

    return () => clearInterval(interval)
  }, [polling, runId])

  const scoreboard = useMemo(() => {
    if (!data?.models) return []
    return data.models.map((model) => ({
      modelId: model.modelId,
      displayName: model.snapshot?.model ?? model.modelId,
      scores: model.scores,
      totals: model.totals,
      latency: model.latency,
    }))
  }, [data])

  if (!runId) {
    return (
      <Container className="py-10">
        <Card className="p-8">
          <h1 className="text-2xl font-semibold text-brand-slate mb-4">AI Lab Results</h1>
          <p className="text-brand-slate-dark">No run selected. Choose a run from the AI Lab dashboard.</p>
          <Button className="mt-4" onClick={() => navigate('/admin/ai-lab')}>
            Back to AI Lab
          </Button>
        </Card>
      </Container>
    )
  }

  if (loading || !data) {
    return (
      <Container>
        <Card className="p-12">
          <Loading text="Loading run results..." />
        </Card>
      </Container>
    )
  }

  const run = data.run

  return (
    <Container className="py-10 space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-semibold text-brand-slate">Run Results</h1>
            {polling && (
              <Badge variant="blue" className="animate-pulse">
                Running...
              </Badge>
            )}
            {run.status === 'completed' && <Badge variant="green">Completed</Badge>}
            {run.status === 'failed' && <Badge variant="red">Failed</Badge>}
          </div>
          <div className="text-brand-slate-dark text-sm mt-2">
            Run ID: <code className="text-xs bg-brand-gray-100 px-2 py-1 rounded">{run.id}</code>
          </div>
          <div className="text-brand-slate-dark text-sm mt-1">
            Started: {formatDateTime(run.started_at)} · Completed: {formatDateTime(run.completed_at)}
          </div>
          {polling && (
            <div className="text-xs text-brand-blue mt-2">
              ⟳ Auto-refreshing every 5 seconds...
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={() => navigate('/admin/ai-lab')}>Back to AI Lab</Button>
          <Button onClick={loadRun}>Refresh</Button>
        </div>
      </div>

      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-brand-slate">Scoreboard</h2>
          <span className="text-xs text-brand-slate-dark">
            {run.model_list.length} models · {run.scenario_list.length} scenarios · {run.association_list.length}{' '}
            associations
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="text-brand-slate-dark">
                <th className="py-2 pr-4">Model</th>
                <th className="py-2 pr-4">Discovery</th>
                <th className="py-2 pr-4">Navigation</th>
                <th className="py-2 pr-4">iCal</th>
                <th className="py-2 pr-4">Extraction</th>
                <th className="py-2 pr-4">Speed</th>
                <th className="py-2 pr-4">Cost</th>
                <th className="py-2 pr-4">Total</th>
              </tr>
            </thead>
            <tbody>
              {scoreboard.map((row) => (
                <tr key={row.modelId} className="border-t border-brand-gray-100 text-brand-slate">
                  <td className="py-2 pr-4">
                    <div className="font-medium text-sm">{row.displayName}</div>
                    <div className="text-xs text-brand-slate-dark break-all">{row.modelId}</div>
                  </td>
                  <td className="py-2 pr-4">{formatNumber(row.scores.discovery)}</td>
                  <td className="py-2 pr-4">{formatNumber(row.scores.navigation)}</td>
                  <td className="py-2 pr-4">{formatNumber(row.scores.ical)}</td>
                  <td className="py-2 pr-4">{formatNumber(row.scores.extraction)}</td>
                  <td className="py-2 pr-4">{formatNumber(row.scores.speed)}</td>
                  <td className="py-2 pr-4">{formatNumber(row.scores.cost)}</td>
                  <td className="py-2 pr-4 font-semibold">{formatNumber(row.scores.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {data.models.map((model) => (
        <Card key={model.modelId} className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-brand-slate">{model.snapshot?.model ?? model.modelId}</h2>
              <div className="text-xs text-brand-slate-dark break-all">{model.modelId}</div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="green">Cost ${formatNumber(model.totals.costUsd, 4)}</Badge>
              <Badge variant="amber">Latency {model.latency.averageMs ? `${model.latency.averageMs} ms` : '—'}</Badge>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {Object.entries(model.scores).map(([key, value]) => (
              key === 'total' ? null : (
                <div key={key} className="bg-brand-gray-50 rounded-lg p-4">
                  <div className="text-sm text-brand-slate-dark">{metricLabels[key] ?? key}</div>
                  <div className="text-2xl font-semibold text-brand-slate mt-1">{formatNumber(value)}</div>
                </div>
              )
            ))}
            <div className="bg-brand-gray-50 rounded-lg p-4">
              <div className="text-sm text-brand-slate-dark">Prompt Tokens</div>
              <div className="text-2xl font-semibold text-brand-slate mt-1">
                {model.totals.promptTokens.toLocaleString()}
              </div>
            </div>
            <div className="bg-brand-gray-50 rounded-lg p-4">
              <div className="text-sm text-brand-slate-dark">Completion Tokens</div>
              <div className="text-2xl font-semibold text-brand-slate mt-1">
                {model.totals.completionTokens.toLocaleString()}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {model.cases.map((caseRecord) => {
              const output = model.outputs.find((item) => item.case_id === caseRecord.id)
              const scenario = data.scenarios.find((item) => item.id === caseRecord.scenario)
              const expanded = expandedCaseId === caseRecord.id
              return (
                <div key={caseRecord.id} className="border border-brand-gray-100 rounded-lg">
                  <button
                    className="w-full flex items-center justify-between text-left p-4"
                    onClick={() => setExpandedCaseId(expanded ? null : caseRecord.id)}
                  >
                    <div>
                      <div className="text-sm font-medium text-brand-slate">
                        {scenario?.name ?? caseRecord.scenario}
                      </div>
                      <div className="text-xs text-brand-slate-dark break-all">
                        {caseRecord.association_url}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-brand-slate-dark">
                      <span>Status: {caseRecord.status}</span>
                      <span>Score: {output ? formatNumber(output.score) : '—'}</span>
                      <span>Latency: {caseRecord.latency_ms ? `${caseRecord.latency_ms} ms` : '—'}</span>
                    </div>
                  </button>
                  {expanded && (
                    <div className="p-4 border-t border-brand-gray-100 space-y-4 bg-brand-gray-50">
                      {output?.parsed_json ? (
                        <div>
                          <div className="text-xs font-semibold text-brand-slate mb-2">Parsed Result</div>
                          {/* Special rendering for team enumeration */}
                          {caseRecord.scenario === 'team-enumeration' && output.parsed_json?.teams ? (
                            <div className="space-y-2">
                              <div className="text-xs text-brand-slate-dark mb-2">
                                Found {output.parsed_json.teams.length} teams
                              </div>
                              <div className="space-y-2">
                                {output.parsed_json.teams.map((team, idx) => (
                                  <div key={idx} className="bg-white p-3 rounded text-xs border border-brand-gray-200">
                                    <div className="font-semibold text-brand-slate">{team.name}</div>
                                    <div className="text-brand-slate-dark mt-1">
                                      URL: <a href={team.team_url} target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:underline">{team.team_url}</a>
                                    </div>
                                    {team.notes && (
                                      <div className="text-brand-slate-dark mt-1">
                                        Notes: {team.notes}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <details className="text-xs">
                                <summary className="cursor-pointer text-brand-blue hover:underline">View Raw JSON</summary>
                                <pre className="bg-white p-3 rounded text-xs overflow-x-auto mt-2">
                                  {JSON.stringify(output.parsed_json, null, 2)}
                                </pre>
                              </details>
                            </div>
                          ) : /* Special rendering for calendar extraction events */
                          caseRecord.scenario === 'calendar-extraction' && output.parsed_json?.events ? (
                            <div className="space-y-2">
                              <div className="text-xs text-brand-slate-dark mb-2">
                                Found {output.parsed_json.events.length} events (via {output.parsed_json.source_type || 'unknown'})
                              </div>
                              <div className="max-h-96 overflow-y-auto space-y-2">
                                {output.parsed_json.events.slice(0, 10).map((event, idx) => (
                                  <div key={idx} className="bg-white p-3 rounded text-xs border border-brand-gray-200">
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <span className="font-semibold">Date:</span> {event.date || 'N/A'}
                                      </div>
                                      <div>
                                        <span className="font-semibold">Time:</span> {event.time || 'N/A'}
                                      </div>
                                      <div>
                                        <span className="font-semibold">Type:</span> {event.type || 'other'}
                                      </div>
                                      <div>
                                        <span className="font-semibold">Location:</span> {event.location || 'N/A'}
                                      </div>
                                      {event.opponent && (
                                        <div className="col-span-2">
                                          <span className="font-semibold">Opponent:</span> {event.opponent}
                                        </div>
                                      )}
                                      {event.notes && (
                                        <div className="col-span-2">
                                          <span className="font-semibold">Notes:</span> {event.notes}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                {output.parsed_json.events.length > 10 && (
                                  <div className="text-xs text-brand-slate-dark text-center py-2">
                                    ... and {output.parsed_json.events.length - 10} more events
                                  </div>
                                )}
                              </div>
                              <details className="text-xs">
                                <summary className="cursor-pointer text-brand-blue hover:underline">View Raw JSON</summary>
                                <pre className="bg-white p-3 rounded text-xs overflow-x-auto mt-2">
                                  {JSON.stringify(output.parsed_json, null, 2)}
                                </pre>
                              </details>
                            </div>
                          ) : (
                            <pre className="bg-white p-3 rounded text-xs overflow-x-auto">
                              {JSON.stringify(output.parsed_json, null, 2)}
                            </pre>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-brand-slate-dark">No parsed output available.</div>
                      )}
                      {output?.validation_errors?.length ? (
                        <div>
                          <div className="text-xs font-semibold text-brand-slate mb-2">Validation Notes</div>
                          <ul className="list-disc pl-5 text-xs text-brand-slate-dark space-y-1">
                            {output.validation_errors.map((note, index) => (
                              <li key={`${caseRecord.id}-note-${index}`}>{note}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {output?.raw_output ? (
                        <div>
                          <div className="text-xs font-semibold text-brand-slate mb-2">Raw Output</div>
                          <pre className="bg-white p-3 rounded text-xs overflow-x-auto">
                            {output.raw_output}
                          </pre>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </Card>
      ))}

      <div className="flex items-center justify-between pb-10">
        <Link className="text-brand-blue text-sm hover:underline" to="/admin/ai-lab">
          ← Back to AI Lab
        </Link>
        <Button onClick={() => navigate('/admin/ai-lab/run')}>Launch Another Run</Button>
      </div>
    </Container>
  )
}

export default AILabResults


